import React, { useMemo } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { generatePath, NavLink, useHistory } from 'react-router-dom';

import GenericListSkeleton from 'flow/components/loaders/GenericListSkeleton';
import Page from 'flow/components/Setting/Page';
import Button from 'flow/components/ui-components/Button';
import ChatAvatar from 'flow/components/ui-components/ChatAvatar';
import Icon from 'flow/components/ui-components/Icon';
import Room from 'flow/components/ui-components/Room';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { Message } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { selectPublicSessions } from 'flow/selectors/chat-selectors';

import { TypedMap } from 'flow/types/TypedMap';
import { getAttachmentLink, getFormattedTime, newestFirst } from 'flow/utils';
import {
    ChatAttachments,
    getAttachmentTypeFromExtension,
    getImageFromChatAttachment,
} from 'flow/utils/message-utils';
import { twMerge } from 'tailwind-merge';
import ChatPostSummary from './ChatPostSummary';
import EmptyState from './EmptyState';

const Overview = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { openModal } = useModal();
    const { isSinglePaneMode } = useSinglePaneMode();

    const sessions = useAppSelector((state) => state.chats.get('sessions'));
    const isPublicMessageLoading = useAppSelector((state) =>
        state.chats.get('publicMessageLoading'),
    );

    const publicSessions = useAppSelector(selectPublicSessions);
    const rooms = useMemo(
        () => publicSessions.sort((a, b) => newestFirst(a, b)).slice(0, 4),
        [publicSessions],
    );

    const unreadMessages = useMemo(
        () =>
            sessions
                .filter((session) => session.get('numberOfUnreadMessages') > 0)
                .sort((a, b) =>
                    a.getIn(['latestMessage', 'sent']) <
                    b.getIn(['latestMessage', 'sent'])
                        ? 1
                        : -1,
                )
                .valueSeq(),
        [sessions],
    );

    const goToCreateMessage = () => {
        history.push(routes['messages.create']);
    };

    return (
        <Page
            header={i18next.t('messages.overview')}
            onClickBack={
                isSinglePaneMode
                    ? () => history.push(routes['messages'])
                    : undefined
            }
            className="bg-gray-150 dark:bg-white"
        >
            <article key={'overview'}>
                <header
                    className="flex h-15 items-center justify-between"
                    key={'overview.header'}
                >
                    <h2 className="text-xl font-bold">
                        {i18next.t('chatlisting.newmessages')}
                    </h2>

                    <Button
                        color="primary"
                        iconKey="add"
                        className="py-1"
                        onClick={goToCreateMessage}
                    >
                        {i18next.t('Chat.Create.Nav.Title')}
                    </Button>
                </header>

                <main className="flex flex-col gap-3">
                    {!isPublicMessageLoading || unreadMessages.size > 0 ? (
                        unreadMessages.size > 0 ? (
                            unreadMessages.map((session) => {
                                const latestMessage: TypedMap<Message> =
                                    session.get('latestMessage');

                                if (
                                    latestMessage?.get('DTOSubtype') ===
                                    'ChatPostDTO'
                                ) {
                                    return (
                                        <ChatPostSummary
                                            key={session.get('key')}
                                            message={latestMessage}
                                            sessionKey={session.get('key')}
                                        />
                                    );
                                }

                                return (
                                    <NavLink
                                        key={session.get('key')}
                                        to={generatePath(
                                            routes['messages.id'],
                                            {
                                                id: session.get('key'),
                                            },
                                        )}
                                        className="shadow-base flex items-start gap-x-3 rounded-sm bg-white p-3 hover:shadow-lg"
                                    >
                                        <ChatAvatar
                                            session={session}
                                            showUnreadBadge={
                                                session.get(
                                                    'numberOfUnreadMessages',
                                                ) > 1
                                            }
                                        />

                                        <div className="w-full">
                                            <div className="flex justify-between">
                                                <p
                                                    className={twMerge(
                                                        'text-base/5',
                                                        session.get(
                                                            'numberOfUnreadMessages',
                                                        ) > 0
                                                            ? 'font-bold'
                                                            : '',
                                                    )}
                                                >
                                                    {session.get('name')}
                                                </p>
                                                <p className="text-sm/4 text-gray-500">
                                                    {getFormattedTime(
                                                        latestMessage?.get(
                                                            'sent',
                                                        ),
                                                        i18next,
                                                    )}
                                                </p>
                                            </div>

                                            <div className="flex flex-col">
                                                <OverviewFilePreview
                                                    message={latestMessage}
                                                    sessionKey={session.get(
                                                        'key',
                                                    )}
                                                />

                                                <div className="flex justify-between">
                                                    <p className="text-sm wrap-anywhere text-gray-500">
                                                        {latestMessage?.getIn([
                                                            'from',
                                                            'contact',
                                                            'firstName',
                                                        ])}
                                                        {': '}
                                                        {latestMessage?.get(
                                                            'message',
                                                        )}
                                                    </p>

                                                    {latestMessage?.get(
                                                        'alsoAsSMS',
                                                    ) ? (
                                                        <Icon
                                                            iconKey="sms"
                                                            className="text-gray-500"
                                                            size="1rem"
                                                        />
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    </NavLink>
                                );
                            })
                        ) : (
                            <EmptyState
                                message={i18next.t(
                                    'EmptyState.NoNewMessages.Title',
                                )}
                            />
                        )
                    ) : (
                        <GenericListSkeleton count={1} />
                    )}
                </main>
            </article>

            <article key={'public-rooms'} className="mt-6">
                <header
                    className="flex h-15 items-center justify-between"
                    key={'overview.header'}
                >
                    <h2 className="text-xl font-bold">
                        {i18next.t('chat.publicRooms')}
                    </h2>
                    {(!isPublicMessageLoading || rooms.size > 0) &&
                        (rooms.size > 0 ? (
                            <Button
                                color="ghost"
                                className="min-h-[32px] !min-w-[82px] py-1"
                                onClick={() => openModal('listRooms')}
                            >
                                {i18next.t('Chat.Public.View')}
                            </Button>
                        ) : (
                            <Button
                                color="ghost"
                                iconKey="add"
                                className="min-h-[32px] !min-w-[82px] py-1"
                                onClick={() => openModal('managePublicRoom')}
                            >
                                {i18next.t('Chat.Public.Create.Done.Title')}
                            </Button>
                        ))}
                </header>
                {!isPublicMessageLoading || rooms.size > 0 ? (
                    rooms.size > 0 ? (
                        <main className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {rooms.map((room) => (
                                <Room
                                    key={room.get('key')}
                                    room={room}
                                    className="shadow-base transition-shadow duration-200 hover:shadow-lg"
                                />
                            ))}
                        </main>
                    ) : (
                        <EmptyState
                            message={i18next.t(
                                'EmptyState.NoPublicRooms.Title',
                            )}
                        />
                    )
                ) : (
                    <GenericListSkeleton isGrid={true} />
                )}
            </article>
        </Page>
    );
};

interface Props {
    message: TypedMap<Message>;
    sessionKey: ChatSessionKey;
}

const OverviewFilePreview = ({ message, sessionKey }: Props) => {
    const attachment = message?.get('attachment');

    if (!attachment) {
        return null;
    }

    const imgSrc = getAttachmentLink(sessionKey, attachment);

    const fileType = getAttachmentTypeFromExtension(attachment.get('fileName'));

    return fileType === ChatAttachments.PHOTO ? (
        <img
            src={imgSrc}
            alt={attachment.get('fileName')}
            className="my-2 max-h-[200px] max-w-[300px] cursor-pointer rounded-sm"
        />
    ) : (
        <div className="my-2 flex w-full items-center gap-x-2 rounded-sm border border-gray-300 p-2">
            <Icon
                iconKey={getImageFromChatAttachment(fileType as any)}
                className="text-gray-600"
            />
            <span className="text-sm wrap-anywhere text-gray-800">
                {attachment.get('fileName')}
            </span>
        </div>
    );
};

export default Overview;
